import moment from 'moment';

export function CommissionPolicyArchiveService() {
    const service = {
        filterArchive: filterArchive,
        addArchive: addArchive,
        deleteArchive: deleteArchive,
        updateCommissionPolicyArchive: updateCommissionPolicyArchive,
        archiveDatesOverlap: archiveDatesOverlap,
        archiveActiveDatesAreLocked: archiveActiveDatesAreLocked,
        filterTier: filterTier,
        updateTiers: updateTiers,
        addTier: addTier,
        deleteTier: deleteTier,
        changeCommissionPolicyDivision: changeCommissionPolicyDivision,
        changeArchiveDivision: changeArchiveDivision
    };

    return service;

    /**
     * Return whether the given archive should be included in the filter.
     * @param archive The CommissionPolicyArchive to be filtered.
     * @returns Boolean
     */
    function filterArchive(archive) {
        return !archive.IsDeleted;
    }

    /**
     * Add a new archive to the given commission policy.
     * @param commissionPolicy The CommissionPolicy to which the archive will be added.
     * @returns The newly created archive.
     */
    function addArchive(commissionPolicy) {
        const newArchive = {
            IsNew: true,
            IsDeleted: false,
            ActiveStart: null,
            ActiveEnd: null,
            Tiers: []
        };

        // Add the new archive to the beginning of the list.
        commissionPolicy.Archives.unshift(newArchive);
        return newArchive;
    }

    /**
     * Toggle deletion for the given archive from the given policy,
     * asking for permission via the confirmation function if one is provided.
     * @param archive The archive to be deleted.
     * @param commissionPolicy The commission policy from which the archive should be removed.
     * @param confirmationFn The function to invoke when asking for confirmation.
     */
    function deleteArchive(archive, commissionPolicy, confirmationFn) {
        confirmationFn = confirmationFn || function () {
            return true;
        };

        if (archive.Locked) {
            throw "Cannot delete this archive because it contains locked tiers.";
        }

        if (archive.IsDeleted || !archive.IsNew && !confirmationFn()) {
            archive.IsDeleted = false;
            return;
        }

        archive.IsDeleted = true;
    }

    /**
     * Close any unspecified end dates in given commission policy that result from changing
     * the given archive.
     * @param commissionPolicy The commission policy containing the archives.
     * @param archive The specific archive that was updated.
     * @returns A list of archives that was updated as a result of this change.
     */
    function updateCommissionPolicyArchive(commissionPolicy, archive) {
        const affectedArchives = [];

        const archives = commissionPolicy.Archives.filter(function (archive) {
            return !archive.IsDeleted;
        });
        const nextArchive = archives.reduce(function (nextArchive, other) {
            // Completely ignore archives that are before the updated one.
            if (other === archive || moment(other.ActiveStart).isSameOrBefore(archive.ActiveStart)) {
                return nextArchive
            }

            if (!nextArchive || moment(nextArchive.ActiveStart).isSameOrAfter(other.ActiveStart)) {
                return other;
            } else {
                return nextArchive;
            }
        }, null);

        if (nextArchive && !archive.ActiveEnd) {
            // Current archive cannot have a null end date, so set it to the day before the next active start.
            archive.ActiveEnd = moment(nextArchive.ActiveStart).subtract(1, "day").toDate();
            affectedArchives.push(archive);
        }

        const previousArchive = archives.reduce(function (previousArchive, other) {
            // Completely ignore archives that are after the updated one.
            if (other === archive || moment(other.ActiveStart).isSameOrAfter(archive.ActiveStart)) {
                return previousArchive
            }

            if (!previousArchive || moment(previousArchive.ActiveStart).isSameOrBefore(other.ActiveStart)) {
                return other;
            } else {
                return previousArchive;
            }
        }, null);

        if (previousArchive && !previousArchive.ActiveEnd) {
            // Set the end for the previous archive.
            previousArchive.ActiveEnd = moment(archive.ActiveStart).subtract(1, "day").toDate();
            affectedArchives.push(previousArchive);
        }

        return affectedArchives;

    }

    /**
     * Return whether the given archive has any date conflicts with other archives in
     * the commission policy.
     * @param archive The CommissionPolicyArchive to check.
     * @param activeStart The new start date.
     * @param activeEnd The new end date.
     * @param commissionPolicy The CommissionPolicy that the archive belongs to.
     * @returns Boolean whether the change is valid.
     */
    function archiveDatesOverlap(archive, activeStart, activeEnd, commissionPolicy) {
        return commissionPolicy.Archives.reduce(function (any, other) {
            return any || other !== archive // Archives can't conflict with themselves, of course.
                && !other.IsDeleted
                && !!other.ActiveStart // Required field, so if this is missing, don't even bother comparing the dates.
                && !!activeStart
                // The end date is not required and can be dropped as low as the start date if there is a conflict.
                && moment(other.ActiveStart).isSameOrBefore(activeEnd || activeStart)
                && moment(activeStart).isSameOrBefore(other.ActiveEnd || other.ActiveStart);
        }, false);
    }

    /**
     * Return whether the given archive can be updated to the given start
     * and end date without conflicting with the given list of payrolls.
     * @param archive The CommissionPolicyArchive to validate.
     * @param startDate The old start date.
     * @param endDate The old end date.
     * @param payrolls The list of the payrolls for the commission policy's company.
     * @returns Boolean whether the change is valid.
     */
    function archiveActiveDatesAreLocked(archive, activeStart, activeEnd, payrolls) {
        const startRange = {
            start: moment.min(archive.ActiveStart, activeStart),
            end: moment.max(archive.ActiveStart, activeStart)
        };
        const endRange = {
            start: moment.min(archive.ActiveEnd, activeEnd),
            end: moment.max(archive.ActiveEnd, activeEnd)
        };
        return !payrolls.reduce(function (any, payroll) {
            if (!payroll.Locked) {
                // Skip check if the payroll is not locked.
                return any;
            }

            // Check if the range [oldStart, newStart] or [oldEnd, newEnd] intersects
            // with any locked payrolls.
            return any
                || !startRange.start.isSame(startRange.end) && startRange.start.isBefore(payroll.EndDate)
                    && moment(payroll.StartDate).isBefore(startRange.end)
                || !endRange.start.isSame(endRange.end) && endRange.start.isBefore(payroll.EndDate)
                    && moment(payroll.StartDate).isBefore(endRange.end);

        }, false);
    }

    /**
     * Return whether the tier should be included in the filter.
     * @param tier The CommissionPolicyTier to be filtered.
     * @returns Boolean
     */
    function filterTier(tier) {
        return !tier.IsDeleted;
    }

    /**
     * Update the tiers in the given archive so the tier numbers progress in order
     * and there are no gaps between tier start and end amounts.
     * @param archive The CommissionPolicyArchive to be updated.
     */
    function updateTiers(archive) {
        const tiers = archive.Tiers.filter(service.filterTier);

        let prevTier = tiers[0];

        if (prevTier) {
            prevTier.TierNo = 1;
            prevTier.StartAmount = 0;
        }

        // Update the tier start and end amounts for each tier so there is no overlap.
        for (let i = 1; i < tiers.length; i++) {
            prevTier = tiers[i - 1];
            const currTier = tiers[i];
            currTier.TierNo = prevTier.TierNo + 1;
            currTier.StartAmount = prevTier.EndAmount + 0.01;
            currTier.EndAmount = Math.max(currTier.EndAmount, currTier.StartAmount);
            if (prevTier.DailyNCRThreshold !== null) {
                currTier.DailyNCRThreshold = Math.max(prevTier.DailyNCRThreshold, currTier.DailyNCRThreshold);
            }
        }
    }

    /**
     * Add a new tier to the end of the tier list for the given archive.
     * @param archive The CommissionPolicyArchive to which a tier will be added.
     * @returns The CommissionPolicyTier that was added.
     */
    function addTier(archive) {
        // Add a new tier to the end of the tier list.
        const tiers = archive.Tiers.filter(service.filterTier);
        const lastTier = tiers[tiers.length - 1];
        const newTier = {
            IsNew: true,
            IsDeleted: false,
            TierNo: tiers.length + 1,
            StartAmount: lastTier ? lastTier.EndAmount + 0.01 : 0,
            EndAmount: lastTier ? lastTier.EndAmount + 0.01 : 100000,
            TierRate: lastTier ? lastTier.TierRate : 0.1,
            DailyNCRThreshold: lastTier ? lastTier.DailyNCRThreshold : null
        }
        archive.Tiers.push(newTier);

        service.updateTiers(archive);

        return newTier;
    }

    /**
     * Delete the given tier from the given archive.
     * @param tier The CommissionPolicyTier to be deleted.
     * @param archive The archive from which the tier should be removed.
     */
    function deleteTier(tier, archive) {
        tier.IsDeleted = true;

        updateTiers(archive);
    }

    /**
     * Update the archives belonging to the given commission policy according
     * to the settings of the given division.
     * @param commissionPolicy The CommissionPolicy to update.
     * @param division The division containing the new settings that will be used.
     */
    function changeCommissionPolicyDivision(commissionPolicy, division) {
        // Once the archive has been saved, changing the division should have no effect.
        const affectedArchives = commissionPolicy.Archives.filter(function (archive) {
            return archive.IsNew;
        });

        affectedArchives.forEach(function (archive) {
            service.changeArchiveDivision(archive, division);
        });
    }

    /**
     * Change the given archive's division to the given division.
     * @param archive The CommissionPolicyArchive to be updated.
     * @param division The division to which the archive will be updated.
     */
    function changeArchiveDivision(archive, division) {
        if (!division) {
            archive.CommissionTierLimit = 0;
            archive.UseCommissionThreshold = false;
        } else {
            archive.CommissionTierLimit = division.CommissionTierLimit;
            archive.UseCommissionThreshold = division.UseCommissionThreshold;
        }

        const tiers = archive.Tiers.filter(service.filterTier);

        for (let i = archive.CommisionTierLimit; i < tiers.length; i++) {
            const tier = tiers[i];
            if (!archive.UseCommissionThreshold) {
                // Clear existing commission threshold if the new program doesn't support it.
                tier.DailyNCRThreshold = null;
            }
            if (archive.CommisionTierLimit !== 0 && i >= archive.CommissionTierLimit) {
                // Remove tiers past the tier limit from the archive.
                service.deleteTier(tier, archive);
            }
        }
    }
}
